import {useAbility} from '@casl/react';
import BankCardIcon from '@raison/static/icons/bank-card-line.svg';
import CustomerServiceIcon from '@raison/static/icons/customer-service-2-line.svg';
import DonutChartIcon from '@raison/static/icons/donut-chart-line.svg';
import FileChartIcon from '@raison/static/icons/file-chart-line.svg';
import FileTextIcon from '@raison/static/icons/file-text-line.svg';
import HistoryIcon from '@raison/static/icons/history-line.svg';
import LogoutIcon from '@raison/static/icons/logout-box-r-line.svg';
import SettingsIcon from '@raison/static/icons/settings-4-line.svg';
import UserIcon from '@raison/static/icons/user-line.svg';
import {MenuElementContext} from '@raison/ui-kit';
import {useAtomValue} from 'jotai';
import React, {useMemo} from 'react';
import {Link} from 'react-router';
import {MenuLayout, NavigationList} from '~/entities/menu-elements';
import {logoutNowEverywhere} from '~/entities/user';
import {VersionDisplay} from '~/processes/app-maintenance';
import {useAppDispatch} from '~/shared/store';
import {TariffMenuBanner} from '../../processes/tariff-management';
import {AppAbilityContext} from '../access-control/AbilityContainer';
import * as api from '../api';
import RaisonIcon from './raison-line.svg';

const publicLinks: NavigationList.Item[] = [
    {
        label: 'Support assistant',
        path: '/support/form',
        icon: CustomerServiceIcon,
    },
    {
        label: 'About Raison',
        path: '/about',
        icon: RaisonIcon,
    },
];

const privateLinks: NavigationList.Item[] = [
    {
        label: 'My Accounts',
        path: '/assets',
        icon: DonutChartIcon,
    },
    {
        label: 'Transactions',
        path: '/transactions',
        icon: HistoryIcon,
    },
    {
        label: 'Reports',
        path: '/reports',
        icon: FileChartIcon,
    },
    {
        label: 'Documents',
        path: '/documents',
        icon: FileTextIcon,
    },
    {
        label: 'My cards',
        path: '/cards',
        icon: BankCardIcon,
    },
    {
        label: 'My Profile',
        path: '/profile',
        icon: UserIcon,
    },
    {
        label: 'Settings',
        path: '/settings',
        icon: SettingsIcon,
    },
    {
        label: 'About Raison',
        path: '/about',
        icon: RaisonIcon,
    },
];

export const MenuProvider = ({children}: {children?: React.ReactNode}) => {
    const ability = useAbility(AppAbilityContext);
    const userIsAuthorized = ability.cannot('view', 'auth');
    const dispatch = useAppDispatch();
    const logout = () => dispatch(logoutNowEverywhere());

    const element = useMemo(() => {
        if (userIsAuthorized) {
            return (
                <MenuLayout
                    banners={
                        <>
                            <TariffBanner />
                            <SupportBanner />
                        </>
                    }
                    footer={<VersionDisplay />}
                >
                    <NavigationList>
                        {privateLinks.map(item => (
                            <NavigationList.Item key={item.label} {...item} />
                        ))}
                        <NavigationList.Item label={'Logout'} icon={LogoutIcon} action={logout} />
                    </NavigationList>
                </MenuLayout>
            );
        } else {
            return (
                <MenuLayout footer={<VersionDisplay />}>
                    <NavigationList>
                        {publicLinks.map(item => (
                            <NavigationList.Item key={item.label} {...item} />
                        ))}
                    </NavigationList>
                </MenuLayout>
            );
        }
    }, [userIsAuthorized]);

    return <MenuElementContext.Provider value={element} children={children} />;
};

const TariffBanner = () => {
    const {data, isSuccess} = useAtomValue(api.userTariff.query);
    if (!isSuccess) {
        return <TariffMenuBanner.Skeleton />;
    }
    return <TariffMenuBanner linkTo='/account-plans' tariff={data} />;
};

const SupportBanner = () => {
    return (
        <Link to={'/support/form'} className={'flex flex-col gap-10 rounded-lg bg-shade-20 p-4'}>
            <svg xmlns='http://www.w3.org/2000/svg' width='36' height='36' fill='none' viewBox='0 0 36 36'>
                <path
                    fill='#0a0a0a'
                    fillOpacity='.04'
                    fillRule='evenodd'
                    d='M18 36c9.941 0 18-8.059 18-18S27.941 0 18 0 0 8.059 0 18s8.059 18 18 18'
                    clipRule='evenodd'
                />
                <path
                    fill='#404040'
                    d='M24.615 14.667h.885a1.667 1.667 0 0 1 1.667 1.666v3.334a1.667 1.667 0 0 1-1.667 1.666h-.885A6.67 6.67 0 0 1 18 27.167V25.5a5 5 0 0 0 5-5v-5a5 5 0 1 0-10 0v5.833h-2.5a1.667 1.667 0 0 1-1.667-1.666v-3.334a1.667 1.667 0 0 1 1.667-1.666h.885a6.667 6.667 0 0 1 13.23 0M10.5 16.333v3.334h.833v-3.334zm14.167 0v3.334h.833v-3.334zm-10.2 4.821.883-1.413c.794.497 1.713.76 2.65.759a5 5 0 0 0 2.65-.76l.883 1.414A6.64 6.64 0 0 1 18 22.167a6.64 6.64 0 0 1-3.533-1.013'
                />
            </svg>
            <div className={'flex flex-col gap-1'}>
                <div className={'fv-text-m-regular text-neutral-60'}>Support assistant</div>
                <div className={'fv-heading-s text-neutral-100'}>Get help anytime</div>
            </div>
        </Link>
    );
};
